// /admin/cost — operator-facing dashboard for the cost telemetry that
// otherwise lives behind `apps/api/scripts/cost-report.ts`. Same
// queries, same numbers, just rendered in the admin shell so checking
// spend doesn't require shelling into the box.
//
// Topline: four cards (all-time / 30d / 7d / 24h). Then a 14-day
// SVG trendline. Then breakdowns by call site, model, user, and
// bond. Untracked models surface as a warning band so the operator
// can patch `apps/api/src/llm/pricing.ts` when a new model lands
// without pricing.

import { createFileRoute } from '@tanstack/react-router';
import { type AdminCostReport, getAdminCostReport } from '../lib/api';

export const Route = createFileRoute('/admin/cost')({
  loader: async () => {
    const report = await getAdminCostReport();
    return { report };
  },
  component: CostPage,
});

function fmtUsd(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n === 0) return '$0';
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}

function CostPage() {
  const { report } = Route.useLoaderData();

  return (
    <section className="space-y-10">
      <header>
        <h2 className="font-display text-2xl text-ash-50 tracking-wide">Cost telemetry</h2>
        <p className="mt-1 font-body text-ash-500 text-sm italic">
          Aggregated from <code>telemetry_event</code>. Local LLM calls (embeddings, NLI) cost
          $0 and don't appear here.
        </p>
      </header>

      <ToplineCards totals={report.totals} />

      <Trendline trend={report.trend14d} />

      {report.untrackedModels.length > 0 && (
        <UntrackedBanner rows={report.untrackedModels} />
      )}

      <Breakdown
        title="By call site"
        rows={report.callSites.map((r) => ({
          label: r.callSite,
          total: r.totalUsd,
          events: r.events,
        }))}
      />

      <Breakdown
        title="By model"
        rows={report.models.map((r) => ({
          label: r.modelId,
          total: r.totalUsd,
          events: r.events,
        }))}
      />

      <Breakdown
        title="By user"
        rows={report.users.map((r) => ({
          label: r.email ?? '(no email)',
          total: r.totalUsd,
          events: r.events,
          extra: `${r.bonds} bond${r.bonds === 1 ? '' : 's'}`,
        }))}
      />

      <Breakdown
        title="By bond"
        rows={report.bonds.map((r) => ({
          label: r.emberName
            ? `${r.emberName} (${r.primaryElement})`
            : `bond ${r.bondId.slice(0, 8)}`,
          total: r.totalUsd,
          events: r.events,
        }))}
      />
    </section>
  );
}

function ToplineCards({ totals }: { totals: AdminCostReport['totals'] }) {
  const order = ['24h', '7d', '30d', 'all_time'];
  const labels: Record<string, string> = {
    '24h': 'Last 24 hours',
    '7d': 'Last 7 days',
    '30d': 'Last 30 days',
    all_time: 'All time',
  };
  const sorted = order
    .map((k) => totals.find((t) => t.scope === k))
    .filter((t): t is AdminCostReport['totals'][number] => Boolean(t));

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:gap-4">
      {sorted.map((t) => (
        <div
          key={t.scope}
          className="rounded-sm border border-paper-800/60 bg-paper-900/40 p-4"
        >
          <p className="font-display text-[0.6rem] text-ash-400 uppercase tracking-[0.32em]">
            {labels[t.scope] ?? t.scope}
          </p>
          <p className="mt-2 font-display font-medium text-ash-50 text-2xl tabular-nums sm:text-3xl">
            {fmtUsd(t.totalUsd)}
          </p>
          <p className="mt-1 font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.3em]">
            {t.events} event{t.events === 1 ? '' : 's'}
          </p>
        </div>
      ))}
    </div>
  );
}

function Trendline({ trend }: { trend: AdminCostReport['trend14d'] }) {
  if (trend.length === 0) {
    return (
      <div className="rounded-sm border border-paper-800/60 bg-paper-900/30 p-5">
        <p className="font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em]">
          Last 14 days
        </p>
        <p className="mt-2 font-body text-ash-300">No events yet.</p>
      </div>
    );
  }

  const values = trend.map((t) => t.totalUsd ?? 0);
  const max = Math.max(...values, 0.0001);
  const W = 720;
  const H = 120;
  const PAD = 8;
  const innerW = W - PAD * 2;
  const innerH = H - PAD * 2;
  const stepX = trend.length > 1 ? innerW / (trend.length - 1) : 0;
  const points = values
    .map((v, i) => {
      const x = PAD + i * stepX;
      const y = PAD + innerH - (v / max) * innerH;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  const areaPoints =
    trend.length > 1
      ? `${PAD},${PAD + innerH} ${points} ${PAD + innerW},${PAD + innerH}`
      : null;

  return (
    <div className="rounded-sm border border-paper-800/60 bg-paper-900/30 p-5">
      <header className="mb-3 flex items-baseline justify-between">
        <p className="font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em]">
          Last 14 days
        </p>
        <p className="font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.3em]">
          peak {fmtUsd(max)}
        </p>
      </header>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block w-full" role="img">
        {areaPoints && (
          <polygon
            points={areaPoints}
            fill="rgb(var(--color-accent, 201 102 63) / 0.15)"
          />
        )}
        <polyline
          fill="none"
          stroke="rgb(var(--color-accent, 201 102 63) / 0.85)"
          strokeWidth={1.5}
          points={points}
        />
        {trend.map((t, i) => {
          const x = PAD + i * stepX;
          const y = PAD + innerH - ((t.totalUsd ?? 0) / max) * innerH;
          return (
            <circle
              key={t.day}
              cx={x}
              cy={y}
              r={2}
              fill="rgb(var(--color-accent, 201 102 63) / 1)"
            >
              <title>
                {t.day}: {fmtUsd(t.totalUsd)} ({t.events} events)
              </title>
            </circle>
          );
        })}
      </svg>
      <div className="mt-2 flex justify-between font-display text-[0.55rem] text-ash-500 uppercase tracking-[0.28em]">
        <span>{trend[0]?.day}</span>
        <span>{trend[trend.length - 1]?.day}</span>
      </div>
    </div>
  );
}

function UntrackedBanner({ rows }: { rows: AdminCostReport['untrackedModels'] }) {
  return (
    <div className="rounded-sm border border-accent/40 bg-paper-900/40 p-4">
      <p className="font-display text-[0.62rem] text-accent-strong uppercase tracking-[0.34em]">
        Models without pricing — patch apps/api/src/llm/pricing.ts
      </p>
      <ul className="mt-2 space-y-1 font-body text-ash-200 text-sm">
        {rows.map((r) => (
          <li key={r.modelId} className="flex justify-between">
            <span>{r.modelId}</span>
            <span className="text-ash-500">
              {r.events} event{r.events === 1 ? '' : 's'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface BreakdownRow {
  label: string;
  total: number | null;
  events: number;
  extra?: string;
}

function Breakdown({ title, rows }: { title: string; rows: BreakdownRow[] }) {
  if (rows.length === 0) {
    return (
      <div>
        <h3 className="font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em]">
          {title}
        </h3>
        <p className="mt-2 font-body text-ash-300 text-sm">No data.</p>
      </div>
    );
  }
  const grandTotal = rows.reduce((sum, r) => sum + (r.total ?? 0), 0);
  return (
    <div>
      <h3 className="mb-3 font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em]">
        {title}
      </h3>
      <ul className="space-y-1">
        {rows.map((r) => {
          const pct = grandTotal > 0 ? ((r.total ?? 0) / grandTotal) * 100 : 0;
          return (
            <li
              key={r.label}
              className="relative overflow-hidden rounded-sm border border-paper-800/40 bg-paper-900/20 p-3"
            >
              {/* Background bar = relative share */}
              <div
                className="absolute inset-y-0 left-0 bg-accent/10"
                style={{ width: `${pct}%` }}
                aria-hidden="true"
              />
              <div className="relative flex items-baseline justify-between gap-3">
                <span className="font-body text-ash-100 text-sm">{r.label}</span>
                <div className="flex items-baseline gap-3 font-display uppercase">
                  {/* Cost is the primary number — bigger, brighter,
                   * tabular-nums so the column reads like a ledger. */}
                  <span className="font-display text-[0.95rem] text-ash-50 tabular-nums sm:text-base">
                    {fmtUsd(r.total)}
                  </span>
                  <span className="font-display text-[0.62rem] text-ash-400 tracking-[0.3em]">
                    {pct.toFixed(1)}%
                  </span>
                  <span className="font-display text-[0.62rem] text-ash-500 tracking-[0.3em]">
                    {r.events}
                  </span>
                  {r.extra && (
                    <span className="font-display text-[0.62rem] text-ash-500 tracking-[0.3em]">
                      {r.extra}
                    </span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
